import { useCallback } from 'react';

import { formatISO } from 'date-fns/formatISO';

import { NotificationType } from '@sg/graphql-schema';

import { adjustWantedTime } from '@order/features-ordering';
import { type OrderingContext } from '@order/features-ordering';

import {
  useOrderFeedbackNotifications,
  useOrderStatusNotificationPreferences,
} from '../../../../hooks';
import { useNotificationType } from '../../../../integrations';
import { useOrderStatusLiveActivity } from '../../../../integrations';

export const usePostOrderNotifications = () => {
  const { scheduleOrderFeedbackNotification } = useOrderFeedbackNotifications();
  const getNotificationType = useNotificationType();

  const { startOrderStatusLiveActivity } = useOrderStatusLiveActivity();
  const { isPushSubscribed } = useOrderStatusNotificationPreferences({ requestPolicy: 'cache-first' }); // prettier-ignore

  const handlePostOrderNotifications = useCallback(
    ({ context }: { context: OrderingContext }) => {
      const { orderId = '', wantedTime = { time: '', offset: 0 }, cart } = context;

      const orderWantedTime = wantedTime.time.slice(0, 19).replace(' ', 'T');
      const adjustedOrderWantedTime = adjustWantedTime(wantedTime);
      const formattedWantedTime = adjustedOrderWantedTime ? formatISO(adjustedOrderWantedTime) : '';

      const { orderChannel = 'pickup', locationName = 'sweetgreen', canTrackOrderStatus } = cart;

      if (isPushSubscribed && wantedTime) {
        void scheduleOrderFeedbackNotification({
          orderId,
          wantedTime: formattedWantedTime,
        });
      }

      const { notificationType } = getNotificationType({
        canTrackOrderStatus,
        wantedTime: orderWantedTime,
      });

      if (notificationType === NotificationType.LiveActivity) {
        void startOrderStatusLiveActivity({
          orderId,
          orderChannel,
          canTrackOrderStatus,
          locationName,
          wantedTime: formattedWantedTime,
        });
      }
    },
    [
      getNotificationType,
      isPushSubscribed,
      scheduleOrderFeedbackNotification,
      startOrderStatusLiveActivity,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { handlePostOrderNotifications };
};
